import React, { createContext, useContext, useState, useEffect } from 'react';
import { AdminUser } from '../services/admin';

interface AuthContextType {
  isAuthenticated: boolean;
  user: AdminUser | null;
  login: (token: string, user: AdminUser) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// 检查token是否过期
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // 转换为毫秒
    const now = Date.now();
    const isValid = now < exp;
    console.log('Token expiration check:', {
      expiration: new Date(exp).toISOString(),
      now: new Date(now).toISOString(),
      isValid
    });
    return !isValid;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    console.log('AuthProvider mounted, checking localStorage...');
    const token = localStorage.getItem('admin_token');
    const storedUser = localStorage.getItem('admin_user');
    console.log('Stored token:', token ? 'Present' : 'Not found');
    console.log('Stored user:', storedUser ? 'Present' : 'Not found');

    if (token && storedUser) {
      try {
        // 检查token是否过期
        if (isTokenExpired(token)) {
          console.log('Token expired or invalid, clearing auth data...');
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          setIsAuthenticated(false);
          setUser(null);
          setIsInitialized(true);
          return;
        }

        const parsedUser = JSON.parse(storedUser);
        console.log('Found valid stored auth data, restoring state...');
        setIsAuthenticated(true);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        setIsAuthenticated(false);
        setUser(null);
      }
    } else {
      console.log('No stored auth data found');
      setIsAuthenticated(false);
      setUser(null);
    }
    setIsInitialized(true);
  }, []);

  const login = (token: string, user: AdminUser) => {
    console.log('Login called with:', { token, user });
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_user', JSON.stringify(user));
    setIsAuthenticated(true);
    setUser(user);
  };

  const logout = () => {
    console.log('Logout called');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setIsAuthenticated(false);
    setUser(null);
  };

  // 如果还没有初始化完成，返回null
  if (!isInitialized) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}; 